import cookies from '@/utils/cookies'
import { stringHelper, roles } from '@tweddle-se-team/common/helpers'
import apiWrapper from '@/utils/apiHelpers/apiWrapper'
import { nodeEnv } from './env'

const { base64Encode } = stringHelper
const { invertedRoles } = roles
const IS_PRODUCTION_SETUP = nodeEnv !== 'local'

/**
 * Return the JWT value from the cookies
 * @param {*} ctx Next.js context object
 * @returns
 */
export const getCookieValue = (ctx, cookieName) => {
  const allCookies = cookies.parse(ctx)
  return allCookies[cookieName] || null
}

const cookieOptions = {
  path: '/',
  httpOnly: true,
  sameSite: 'Strict',
  secure: IS_PRODUCTION_SETUP
}

export const setJwtCookie = (ctx, jwt, options = {}) => {
  cookies.set(ctx, 'jwt', jwt, {
    ...cookieOptions,
    ...options
  })
}
export const setRefreshTokenCookie = (ctx, refreshToken, options = {}) =>
  cookies.set(ctx, 'refreshToken', refreshToken, {
    ...cookieOptions,
    ...options
  })

export const clearSessionCookie = ctx => {
  cookies.destroy(ctx, 'jwt', cookieOptions)
  cookies.destroy(ctx, 'refreshToken', cookieOptions)
}

/**
 * Get's the user data from the JWT in jwt cookie to see if the user is authenticated
 * Calls auth/session endpoint to verify the cookie is valid,
 * gets user information from the server
 * and sets it in the store
 * @param {*} ctx
 * @returns Logged in user data or null if not authenticated
 */
export const getUserFromCookie = async ctx => {
  const jwt = getCookieValue(ctx, 'jwt')
  if (!jwt) {
    return {}
  }
  const sessionData = await apiWrapper.get('auth/session', {}, ctx)
  const { user } = sessionData || {}
  if (user) {
    return { user, session: jwt }
  }
  return {}
}

/**
 * Refreshes the JWT token using the refresh token stored in cookies
 * This should only be called on the server side.
 * @param {*} ctx
 * @returns null or { user, jwt }
 */
const refreshTokenSSR = async ctx => {
  try {
    const refreshToken = getCookieValue(ctx, 'refreshToken')
    if (!refreshToken) {
      return null
    }
    const session = await apiWrapper.post('auth/refresh', { refreshToken }, ctx)

    setJwtCookie(ctx, session.jwt, { maxAge: session.jwtExpiresIn })

    return session
  } catch (error) {
    console.log('Not able to refresh token', error)
    return null
  }
}

/**
 * Checks if the user is authorized based on their roles
 * @param {*} user User object from getUserFromCookie
 * @param {*} authorizedRoles Array of roles that are allowed to access the page
 * @returns boolean
 */
const isAuthorized = (user = {}, authorizedRoles) =>
  user &&
  user._id &&
  (!authorizedRoles ||
    authorizedRoles.length === 0 ||
    (user.roles && authorizedRoles.some(r => user.roles.includes(r))))

/**
 * Wrapper for getServerSideProps that checks if the user is authenticated and authorized
 * @param {*} gssp page's getServerSideProps function
 * @param {*} authorizedRoles Array of roles that are allowed to access the page
 * @returns Component's server side props function result merged with authentication and authorization status
 */
export function withAuthSSR(gssp, authorizedRoles = []) {
  return async ctx => {
    const {
      req: { url }
    } = ctx

    // Gets the user from the cookie a validates against server
    const { user } = await getUserFromCookie(ctx)
    const refreshToken = getCookieValue(ctx, 'refreshToken')
    let loggedInUser = user

    if (!user && refreshToken) {
      console.log('User not found in cookie, trying to refresh token')
      // Try to refresh the token if not authenticated
      const session = await refreshTokenSSR(ctx)

      // We have to manually update the cookies in the request headers
      // because even though we set cookie in this method,
      // when we do gssp the original context cookies are still used.
      const updatedCookies = {
        ...cookies.parse(ctx),
        jwt: session.jwt
      }
      ctx.req.headers.cookie = Object.entries(updatedCookies)
        .map(([key, val]) => `${key}=${val}`)
        .join('; ')
      if (session) {
        loggedInUser = session.user
      }
    }

    // If not a valid user redirects to login page
    if (!loggedInUser) {
      const redirect = base64Encode(url)
      return {
        redirect: {
          destination: `/login?redirect=${redirect}`,
          permanent: false
        }
      }
    }

    // Is logged in but doesn't have the required roles
    if (!isAuthorized(loggedInUser, authorizedRoles)) {
      return {
        props: {
          authenticated: true,
          authorized: false
        }
      }
    }

    // If the user is authenticated and authorized, call the original gssp function
    const gsspData = await gssp(ctx)

    return {
      ...gsspData,
      props: {
        ...(gsspData.props || {}),
        authenticated: true,
        authorized: true,
        user: loggedInUser
      }
    }
  }
}

export const getSystemRoles = userRoles => userRoles.map(role => invertedRoles[role])
